import React, { useState, useEffect, useCallback, useRef } from "react";
import CRMLeadForm from "./CRMLeadForm";
import CRMPipelineView from "./CRMPipelineView";
import TrainingGuide from "../common/TrainingGuide";
import PhoneLauncher from "./PhoneLauncher";
import crmSteps from "../../data/training/crm";
import TableMobile, { TableMobileColumn, StatusTone } from "../responsive-fixes/TableMobile";
import { useResponsiveLayout } from "../responsive-fixes/useResponsiveLayout";

// ── Types ──────────────────────────────────────────────────────────────────────

interface Lead {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  country?: string;
  language?: string;
  source?: string;
  status: string;
  importance: string;
  category: string;
  color_label: string;
  tags: string[];
  estimated_value: number;
  notes?: string;
  ai_summary?: string;
  ai_score: number;
  assigned_to?: string;
  media_attachments?: any[];
  created_at: string;
  updated_at: string;
  last_activity_at?: string;
}

interface Activity {
  id: string;
  lead_id: string;
  activity_type: string;
  content: string;
  actor: string;
  metadata?: any;
  created_at: string;
}

interface BridgeDevice {
  serial: string;
  name: string;
  model: string;
  transport: string;
  authorized: boolean;
  connected: boolean;
  source: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const IMPORTANCE_CONFIG: Record<string, { icon: string; label: string; cls: string }> = {
  critical: { icon: "🔴", label: "Critical", cls: "imp-critical" },
  high:     { icon: "🟠", label: "High",     cls: "imp-high"     },
  medium:   { icon: "🔵", label: "Medium",   cls: "imp-medium"   },
  low:      { icon: "⚫", label: "Low",      cls: "imp-low"      },
};

const CATEGORY_CONFIG: Record<string, { icon: string; label: string }> = {
  retail:       { icon: "🛍️",  label: "Retail"       },
  wholesale:    { icon: "📦",  label: "Wholesale"     },
  distributor:  { icon: "🌐",  label: "Distributor"  },
  b2b:          { icon: "🏢",  label: "B2B"          },
  hair_product: { icon: "💇",  label: "Hair Product"  },
  other:        { icon: "📋",  label: "Other"         },
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new:       { label: "New",       color: "#64748b" },
  contacted: { label: "Contacted", color: "#3b82f6" },
  qualified: { label: "Qualified", color: "#8b5cf6" },
  proposal:  { label: "Proposal",  color: "#f59e0b" },
  won:       { label: "Won",       color: "#22c55e" },
  lost:      { label: "Lost",      color: "#ef4444" },
};

const ACTIVITY_ICONS: Record<string, string> = {
  created:       "✨",
  note:          "📝",
  call:          "📞",
  email:         "✉️",
  whatsapp:      "💬",
  status_change: "🔄",
  ai_analysis:   "🤖",
  media:         "📎",
};

const COLOR_PALETTE = ["red","orange","green","blue","purple","gray"];

const SHORTCUTS = [
  { key: "N",          desc: "New lead"                    },
  { key: "F  or  /",   desc: "Focus search"                },
  { key: "1–6",        desc: "Filter by pipeline stage"    },
  { key: "↑↓",         desc: "Navigate list"               },
  { key: "Enter",      desc: "Open selected lead"          },
  { key: "Ctrl+Enter", desc: "Save / submit form"          },
  { key: "Esc",        desc: "Close / clear"               },
  { key: "D",          desc: "Delete selected lead"        },
];

// Source display config
const SOURCE_LABELS: Record<string, { icon: string; label: string }> = {
  "whatsapp-history-import": { icon: "💬", label: "WA Import" },
  "whatsapp":                { icon: "💬", label: "WhatsApp" },
  "website":                 { icon: "🌐", label: "Website" },
  "referral":                { icon: "🤝", label: "Referral" },
  "shopify":                 { icon: "🛒", label: "Shopify" },
  "email":                   { icon: "✉️",  label: "Email" },
  "manual":                  { icon: "✍️",  label: "Manual" },
};

// Relative time formatter
function relTime(iso: string | undefined): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  const wks = Math.floor(days / 7);
  if (wks < 5)    return `${wks}w ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// Group activities by date label
function dateGroup(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString())     return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  const diff = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (diff < 7) return d.toLocaleDateString("en-GB", { weekday: "long" });
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Main component ─────────────────────────────────────────────────────────────

const CRMLeadsList: React.FC = () => {
  const responsive = useResponsiveLayout();
  const useMobileTable = responsive.isPhone || responsive.isPhablet;
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [leads, setLeads]               = useState<Lead[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [showForm, setShowForm]         = useState(false);
  const [editingLead, setEditingLead]   = useState<Lead | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedIdx, setSelectedIdx]   = useState<number>(-1);
  const [activities, setActivities]     = useState<Activity[]>([]);
  const [actLoading, setActLoading]     = useState(false);
  const [newNote, setNewNote]           = useState("");
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showTips, setShowTips]         = useState(false);
  const [showPhonePanel, setShowPhonePanel] = useState(false);

  // ── Phone Bridge (inline call from lead detail) ────────────────────────────
  const BRIDGE_URL = "http://localhost:8765";

  const [bridgeDevices,   setBridgeDevices]   = useState<BridgeDevice[]>([]);
  const [bridgeDevice,    setBridgeDevice]    = useState<string>("");
  const [bridgeOnline,    setBridgeOnline]    = useState<boolean | null>(null);
  const [bridgeCallState, setBridgeCallState] = useState<"idle" | "dialing" | "active">("idle");
  const [bridgeCallNum,   setBridgeCallNum]   = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    const fetchBridgeDevices = async () => {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 3000);
        const res = await fetch(`${BRIDGE_URL}/devices`, { signal: controller.signal });
        clearTimeout(timer);
        if (!res.ok) throw new Error("bridge error");
        const data: BridgeDevice[] = await res.json();
        if (cancelled) return;
        setBridgeDevices(data);
        setBridgeOnline(true);
        if (data.length > 0) {
          setBridgeDevice(prev => {
            if (prev && data.find(d => d.serial === prev)) return prev;
            const auth = data.find(d => d.authorized && d.connected);
            return auth?.serial || data[0].serial;
          });
        }
      } catch {
        if (!cancelled) { setBridgeOnline(false); setBridgeDevices([]); }
      }
    };
    fetchBridgeDevices();
    const id = setInterval(fetchBridgeDevices, 15_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const handleBridgeCall = async (phoneNumber: string) => {
    if (!phoneNumber || !bridgeDevice) return;
    setBridgeCallState("dialing");
    setBridgeCallNum(phoneNumber);
    try {
      const clean = phoneNumber.replace(/[^\d+]/g, "");
      const res = await fetch(`${BRIDGE_URL}/call/${clean}?device_id=${encodeURIComponent(bridgeDevice)}`, { method: "POST" });
      if (res.ok) setBridgeCallState("active");
      else setBridgeCallState("idle");
    } catch {
      setBridgeCallState("idle");
    }
  };

  const handleBridgeEndCall = async () => {
    try {
      await fetch(`${BRIDGE_URL}/end-call?device_id=${encodeURIComponent(bridgeDevice)}`, { method: "POST" });
    } catch {}
    setBridgeCallState("idle");
    setBridgeCallNum("");
  };

  // ── Bulk select + send ───────────────────────────────────────────────────────
  const [checkedLeads,  setCheckedLeads]   = useState<Set<string>>(new Set());
  const [showSendModal, setShowSendModal]  = useState(false);
  const [bulkMsg,       setBulkMsg]        = useState("");
  const [bulkImageFile, setBulkImageFile]  = useState<File | null>(null);
  const [bulkImageUrl,  setBulkImageUrl]   = useState<string>("");
  const [bulkSending,   setBulkSending]    = useState(false);
  const [bulkResult,    setBulkResult]     = useState<{
    sent: number;
    failed: number;
    results?: Array<{
      lead_id: string;
      name?: string;
      phone?: string;
      status: "sent" | "not_found" | "no_phone" | "failed" | "error";
      error?: string;
    }>;
  } | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const toggleCheck = (id: string) => {
    setCheckedLeads(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (checkedLeads.size === leads.length) {
      setCheckedLeads(new Set());
    } else {
      setCheckedLeads(new Set(leads.map(l => l.id)));
    }
  };

  const handleImagePick = async (file: File) => {
    setBulkImageFile(file);
    setBulkImageUrl("");
    // Upload immediately to get a Redis-backed URL
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch("/api/crm/bulk-send/upload-image", { method: "POST", body: fd });
      if (r.ok) {
        const data = await r.json();
        setBulkImageUrl(data.url);
      }
    } catch { /* handled at send time */ }
  };

  const handleBulkSend = async () => {
    if (!bulkMsg && !bulkImageUrl) return;
    setBulkSending(true);
    setBulkResult(null);
    try {
      const r = await fetch("/api/crm/bulk-send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lead_ids:  Array.from(checkedLeads),
          message:   bulkMsg,
          image_url: bulkImageUrl || undefined,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        setBulkResult({
          sent: data.sent,
          failed: data.failed,
          results: data.results,
        });
        setCheckedLeads(new Set());
        setBulkMsg("");
        setBulkImageFile(null);
        setBulkImageUrl("");
      }
    } finally {
      setBulkSending(false);
    }
  };

  // Search / filter state
  const [q, setQ]                   = useState("");
  const [statusFilter, setStatus]   = useState("");
  const [importanceFilter, setImp]  = useState("");
  const [categoryFilter, setCat]    = useState("");
  const [countryFilter, setCountry] = useState("");
  const [colorFilter, setColor]     = useState("");
  const [skip, setSkip]             = useState(0);
  const [total, setTotal]           = useState(0);
  const LIMIT = 50;

  const searchRef = useRef<HTMLInputElement>(null);
  const listRef   = useRef<HTMLTableSectionElement>(null);

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q)               params.set("q", q);
      if (statusFilter)    params.set("status", statusFilter);
      if (importanceFilter) params.set("importance", importanceFilter);
      if (categoryFilter)  params.set("category", categoryFilter);
      if (countryFilter)   params.set("country", countryFilter);
      if (colorFilter)     params.set("color_label", colorFilter);
      params.set("skip", String(skip));
      params.set("limit", String(LIMIT));

      const endpoint = (q || statusFilter || importanceFilter || categoryFilter || countryFilter || colorFilter)
        ? `/api/crm/leads/search?${params}`
        : `/api/crm/leads?${params}`;

      const res = await fetch(endpoint);
      if (!res.ok) throw new Error("Failed to fetch leads");
      const data: Lead[] = await res.json();
      setLeads(data);
      setTotal(data.length < LIMIT ? skip + data.length : skip + LIMIT + 1);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, importanceFilter, categoryFilter, countryFilter, colorFilter, skip]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setSkip(0); fetchLeads(); }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const fetchActivities = async (leadId: string) => {
    setActLoading(true);
    try {
      const res = await fetch(`/api/crm/leads/${leadId}/activities`);
      const data: Activity[] = await res.json();
      setActivities(data);
    } catch { setActivities([]); }
    finally { setActLoading(false); }
  };

  const openLead = (lead: Lead, idx: number) => {
    setSelectedLead(lead);
    setSelectedIdx(idx);
    fetchActivities(lead.id);
  };

  // ── Keyboard shortcuts ───────────────────────────────────────────────────────

  // 1-6 → pipeline stage filter
  const STAGE_KEYS: Record<string, string> = {
    "1": "new", "2": "contacted", "3": "qualified",
    "4": "proposal", "5": "won", "6": "lost",
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      const inInput = ["INPUT","TEXTAREA","SELECT"].includes(tag);

      if (e.key === "Escape") {
        if (selectedLead) { setSelectedLead(null); return; }
        if (showForm)      { setShowForm(false);    return; }
        if (q)             { setQ("");              return; }
      }
      if (inInput) return;

      if (e.key === "n" || e.key === "N") { e.preventDefault(); setEditingLead(null); setShowForm(true); }
      if (e.key === "/" || e.key === "f" || e.key === "F") { e.preventDefault(); searchRef.current?.focus(); }

      // Ctrl+Enter: submit open form
      if (e.ctrlKey && e.key === "Enter") {
        const btn = document.querySelector<HTMLButtonElement>(".crm-form button[type=submit]");
        if (btn) { e.preventDefault(); btn.click(); }
        return;
      }

      // 1-6: switch pipeline stage filter (press same key again to clear)
      if (STAGE_KEYS[e.key] !== undefined) {
        e.preventDefault();
        setStatus((prev: string) => prev === STAGE_KEYS[e.key] ? "" : STAGE_KEYS[e.key]);
        setSkip(0);
      }
      if (e.key === "ArrowDown" && leads.length > 0) {
        e.preventDefault();
        const ni = Math.min(selectedIdx + 1, leads.length - 1);
        openLead(leads[ni], ni);
      }
      if (e.key === "ArrowUp" && leads.length > 0) {
        e.preventDefault();
        const ni = Math.max(selectedIdx - 1, 0);
        openLead(leads[ni], ni);
      }
      if (e.key === "Enter" && selectedLead) {
        setEditingLead(selectedLead);
        setShowForm(true);
      }
      if ((e.key === "d" || e.key === "D") && selectedLead) {
        handleDelete(selectedLead.id);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [leads, selectedIdx, selectedLead, showForm, q]);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this lead?")) return;
    await fetch(`/api/crm/leads/${id}`, { method: "DELETE" });
    setSelectedLead(null);
    fetchLeads();
  };

  const handleAddNote = async () => {
    if (!selectedLead || !newNote.trim()) return;
    await fetch(`/api/crm/leads/${selectedLead.id}/activities`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ activity_type: "note", content: newNote, actor: "sales-team" }),
    });
    setNewNote("");
    fetchActivities(selectedLead.id);
  };

  const handleStatusChange = async (leadId: string, status: string) => {
    await fetch(`/api/crm/leads/${leadId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    fetchLeads();
    if (selectedLead?.id === leadId) setSelectedLead({ ...selectedLead, status });
  };

  const clearFilters = () => {
    setQ(""); setStatus(""); setImp(""); setCat(""); setCountry(""); setColor(""); setSkip(0);
  };
  const hasFilters = q || statusFilter || importanceFilter || categoryFilter || countryFilter || colorFilter;

  const fmt = (n: number) => new Intl.NumberFormat("en-AE", { style: "currency", currency: "AED", minimumFractionDigits: 0 }).format(n);
  const fmtDate = (s: string) => new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const fmtTime = (s: string) => new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  const waLink  = (phone?: string) => phone ? `https://wa.me/${phone.replace(/\D/g,"")}` : null;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="crm-root">

      {/* ── Header ── */}
      <div className="crm-topbar">
        <div className="crm-topbar-left">
          <h1 className="crm-title">CRM</h1>
          <span className="crm-subtitle">Lead Intelligence</span>
        </div>
        <div className="crm-topbar-right">
          <button className="crm-btn-ghost" onClick={() => setShowTips(!showTips)} title="Tips">💡</button>
          <button className="crm-btn-ghost" onClick={() => setShowShortcuts(!showShortcuts)} title="Keyboard shortcuts">⌨️</button>
          <button
            className={showPhonePanel ? "crm-btn-primary" : "crm-btn-ghost"}
            onClick={() => setShowPhonePanel(p => !p)}
            title="Phone mirror (USB + ADB)"
          >📱 Phone</button>
          <button className="crm-btn-ghost" onClick={() => setTrainingOpen(true)} title="Open training guide">?</button>
          <button className="crm-btn-primary" onClick={() => { setEditingLead(null); setShowForm(true); }}>
            + New Lead <span className="crm-shortcut-hint">N</span>
          </button>
        </div>
        <TrainingGuide moduleKey="crm" steps={crmSteps} isOpen={trainingOpen} onClose={() => setTrainingOpen(false)} />
      </div>

      {/* ── Pipeline KPIs ── */}
      <CRMPipelineView onRefresh={fetchLeads} />

      {/* ── Tips banner ── */}
      {showTips && (
        <div className="crm-tips-banner">
          <strong>💡 Tips:</strong>
          <span>AI Score (0–100) = lead hotness.</span>
          <span>🔴 Critical = distributor deal.</span>
          <span>🟠 High = wholesale/B2B.</span>
          <span>Click a row to see history & add notes.</span>
          <span>Use <kbd>/</kbd> to search, <kbd>N</kbd> for new lead.</span>
          <button className="crm-tips-close" onClick={() => setShowTips(false)}>×</button>
        </div>
      )}

      {/* ── Shortcuts overlay ── */}
      {showShortcuts && (
        <div className="crm-overlay" onClick={() => setShowShortcuts(false)}>
          <div className="crm-shortcuts-panel" onClick={e => e.stopPropagation()}>
            <div className="crm-shortcuts-header">
              <span>⌨️ Keyboard Shortcuts</span>
              <button onClick={() => setShowShortcuts(false)}>×</button>
            </div>
            {SHORTCUTS.map(s => (
              <div key={s.key} className="crm-shortcut-row">
                <kbd className="crm-kbd">{s.key}</kbd>
                <span>{s.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Search + Filter bar ── */}
      <div className="crm-search-bar">
        <div className="crm-search-input-wrap">
          <span className="crm-search-icon">🔍</span>
          <input
            ref={searchRef}
            className="crm-search-input"
            placeholder="Search name, company, phone, notes, AI summary… (press /)"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          {q && <button className="crm-search-clear" onClick={() => setQ("")}>×</button>}
        </div>

        <select className="crm-filter-select" value={statusFilter} onChange={e => { setStatus(e.target.value); setSkip(0); }}>
          <option value="">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>

        <select className="crm-filter-select" value={importanceFilter} onChange={e => { setImp(e.target.value); setSkip(0); }}>
          <option value="">All Importance</option>
          {Object.entries(IMPORTANCE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>

        <select className="crm-filter-select" value={categoryFilter} onChange={e => { setCat(e.target.value); setSkip(0); }}>
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>

        <input
          className="crm-filter-select"
          placeholder="Country…"
          value={countryFilter}
          onChange={e => { setCountry(e.target.value); setSkip(0); }}
          style={{ width: 110 }}
        />

        <select className="crm-filter-select" value={colorFilter} onChange={e => { setColor(e.target.value); setSkip(0); }}>
          <option value="">All Colors</option>
          {COLOR_PALETTE.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {hasFilters && (
          <button className="crm-btn-ghost crm-clear-filters" onClick={clearFilters}>
            Clear ×
          </button>
        )}
      </div>

      {/* ── Main layout: table + detail panel ── */}
      <div className="crm-body">

        {/* Table */}
        <div className={`crm-table-wrap ${selectedLead || showPhonePanel ? "crm-table-narrow" : ""}`}>
          {loading ? (
            <div className="crm-loading"><div className="spinner" /></div>
          ) : error ? (
            <div className="crm-error">⚠️ {error} <button onClick={fetchLeads}>Retry</button></div>
          ) : leads.length === 0 ? (
            <div className="crm-empty">
              <div>📋</div>
              <div>{hasFilters ? "No leads match your filters" : "No leads yet"}</div>
              {hasFilters && <button className="crm-btn-ghost" onClick={clearFilters}>Clear filters</button>}
            </div>
          ) : useMobileTable ? (
            // M3 fix (audit): below md the desktop table is unreadable.
            // Render card-view via TableMobile. Desktop table stays untouched.
            <TableMobile<any>
              columns={[
                { key: "name", label: "Name", primary: true,
                  format: (_v: unknown, row: Lead) => (
                    <span>
                      {(IMPORTANCE_CONFIG[row.importance] || IMPORTANCE_CONFIG.medium).icon}{" "}
                      {row.name}
                      {row.company ? ` · ${row.company}` : ""}
                    </span>
                  ),
                },
                { key: "status", label: "Status", status: true,
                  format: (_v: unknown, row: Lead) => (STATUS_CONFIG[row.status] || STATUS_CONFIG.new).label,
                },
                { key: "category", label: "Category",
                  format: (_v: unknown, row: Lead) => {
                    const c = CATEGORY_CONFIG[row.category] || CATEGORY_CONFIG.other;
                    return `${c.icon} ${c.label}`;
                  },
                },
                { key: "country", label: "Country", format: (v: unknown) => (v as string) || "—" },
                { key: "estimated_value", label: "Value",
                  format: (v: unknown) => (v as number) > 0 ? fmt(v as number) : "—",
                },
                { key: "ai_score", label: "Score",
                  format: (v: unknown) => `${(v as number).toFixed(0)}/100`,
                },
                { key: "assigned_to", label: "Agent", format: (v: unknown) => (v as string) || "—" },
                { key: "last_activity_at", label: "Activity",
                  format: (_v: unknown, row: Lead) => relTime(row.last_activity_at || row.created_at),
                },
              ] as TableMobileColumn<any>[]}
              rows={leads}
              statusTone={(row: Lead): StatusTone => {
                const s = row.status;
                if (s === "won") return "success";
                if (s === "lost") return "danger";
                if (s === "proposal") return "warning";
                if (s === "qualified") return "info";
                return "muted";
              }}
              onRowClick={(row: Lead) => {
                const idx = leads.findIndex((l) => l.id === row.id);
                openLead(row, idx);
              }}
              renderActions={(row: Lead) => (
                <>
                  <button
                    className="crm-icon-btn"
                    title="Edit"
                    onClick={() => { setEditingLead(row); setShowForm(true); }}
                  >✏️</button>
                  <button
                    className="crm-icon-btn crm-icon-del"
                    title="Delete"
                    onClick={() => handleDelete(row.id)}
                  >🗑️</button>
                </>
              )}
            />
          ) : (
            <table className="crm-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={checkedLeads.size === leads.length && leads.length > 0}
                      onChange={toggleAll}
                      title="Select all"
                      style={{ cursor: "pointer" }}
                    />
                  </th>
                  <th style={{ width: 28 }}></th>
                  <th>Name</th>
                  {!selectedLead && <><th>Category</th><th>Country</th><th>Tags</th><th>Value</th><th>Agent</th></>}
                  <th>Score</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th style={{ width: 70 }}>Actions</th>
                </tr>
              </thead>
              <tbody ref={listRef}>
                {leads.map((lead, idx) => {
                  const imp  = IMPORTANCE_CONFIG[lead.importance] || IMPORTANCE_CONFIG.medium;
                  const cat  = CATEGORY_CONFIG[lead.category]     || CATEGORY_CONFIG.other;
                  const stat = STATUS_CONFIG[lead.status]         || STATUS_CONFIG.new;
                  const isSelected = selectedLead?.id === lead.id;
                  return (
                    <tr
                      key={lead.id}
                      className={`crm-row crm-color-${lead.color_label} ${isSelected ? "crm-row-selected" : ""} ${checkedLeads.has(lead.id) ? "crm-row-checked" : ""}`}
                      onClick={() => openLead(lead, idx)}
                    >
                      <td onClick={e => e.stopPropagation()} style={{ cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={checkedLeads.has(lead.id)}
                          onChange={() => toggleCheck(lead.id)}
                          onClick={e => e.stopPropagation()}
                          style={{ cursor: "pointer" }}
                        />
                      </td>
                      <td>
                        <span title={imp.label} style={{ fontSize: 14 }}>{imp.icon}</span>
                      </td>
                      <td>
                        <div className="crm-name-cell">
                          <span className="crm-lead-name">{lead.name}</span>
                          {lead.company && <span className="crm-lead-company">{lead.company}</span>}
                        </div>
                      </td>
                      {!selectedLead && (
                        <>
                          <td>
                            <span className="crm-category-badge">
                              {cat.icon} {cat.label}
                            </span>
                          </td>
                          <td className="crm-muted">{lead.country || "—"}</td>
                          <td>
                            <div className="crm-tags">
                              {(lead.tags || []).slice(0, 3).map(t => (
                                <span key={t} className="crm-tag">{t}</span>
                              ))}
                            </div>
                          </td>
                          <td className="crm-mono">{lead.estimated_value > 0 ? fmt(lead.estimated_value) : "—"}</td>
                          <td className="crm-muted">{lead.assigned_to || "—"}</td>
                        </>
                      )}
                      <td>
                        <div className="crm-score-wrap">
                          <div className="crm-score-bar">
                            <div
                              className="crm-score-fill"
                              style={{ width: `${lead.ai_score}%`, background: lead.ai_score >= 75 ? "#ef4444" : lead.ai_score >= 50 ? "#f59e0b" : "#3b82f6" }}
                            />
                          </div>
                          <span className="crm-score-num">{lead.ai_score.toFixed(0)}</span>
                        </div>
                      </td>
                      <td>
                        <span className="crm-status-dot" style={{ background: stat.color }} title={stat.label} />
                        <span className="crm-muted" style={{ fontSize: 12 }}>{stat.label}</span>
                      </td>
                      <td className="crm-muted" style={{ fontSize: 11 }}>
                        <span title={fmtDate(lead.created_at)}>{relTime(lead.last_activity_at || lead.created_at)}</span>
                        {lead.source && SOURCE_LABELS[lead.source] && (
                          <div style={{ marginTop: 2 }}>
                            <span className="crm-source-badge" title={lead.source}>
                              {SOURCE_LABELS[lead.source].icon} {SOURCE_LABELS[lead.source].label}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="crm-row-actions" onClick={e => e.stopPropagation()}>
                          <button className="crm-icon-btn" title="Edit (Enter)" onClick={() => { setEditingLead(lead); setShowForm(true); }}>✏️</button>
                          <button className="crm-icon-btn crm-icon-del" title="Delete (D)" onClick={() => handleDelete(lead.id)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {leads.length > 0 && (
            <div className="crm-pagination">
              <button className="crm-btn-ghost" onClick={() => setSkip(Math.max(0, skip - LIMIT))} disabled={skip === 0}>← Prev</button>
              <span className="crm-muted">Showing {skip + 1}–{skip + leads.length}</span>
              <button className="crm-btn-ghost" onClick={() => setSkip(skip + LIMIT)} disabled={leads.length < LIMIT}>Next →</button>
            </div>
          )}
        </div>

        {/* ── Detail panel ── */}
        {/* 📱 Phone mirror panel */}
        {showPhonePanel && (
          <div style={{
            width: 380, flexShrink: 0, borderLeft: "1px solid #1e293b",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
            <PhoneLauncher />
          </div>
        )}

        {selectedLead && (
          <div className="crm-detail-panel">
            <div className="crm-detail-header">
              <div>
                <div className="crm-detail-name">{selectedLead.name}</div>
                {selectedLead.company && <div className="crm-muted" style={{ fontSize: 13 }}>{selectedLead.company}</div>}
              </div>
              <button className="crm-btn-ghost" onClick={() => setSelectedLead(null)}>×</button>
            </div>

            {/* Key fields */}
            <div className="crm-detail-meta">
              {selectedLead.phone && (
                <div>
                  <span className="crm-label">📱</span>
                  {selectedLead.phone}
                  {waLink(selectedLead.phone) && (
                    <a
                      href={waLink(selectedLead.phone)!}
                      target="_blank"
                      rel="noreferrer"
                      className="crm-wa-link"
                      title="Open in WhatsApp"
                    >↗ WhatsApp</a>
                  )}
                </div>
              )}

              {/* ── Phone Bridge Call Widget ── */}
              {selectedLead.phone && (
                <div style={{
                  margin: "8px 0 4px", padding: "10px 12px", borderRadius: 10,
                  background: "rgba(255,255,255,0.02)",
                  border: "0.5px solid rgba(189,178,255,0.15)",
                  backdropFilter: "blur(60px)",
                }}>
                  {bridgeOnline === false ? (
                    <div style={{ fontSize: 11, color: "#6d758b", lineHeight: 1.7 }}>
                      <span style={{ fontSize: 13 }}>📱</span> Phone bridge offline
                      <div style={{ marginTop: 4, color: "#4b5563" }}>
                        Start: <code style={{ background: "#1e293b", padding: "1px 6px", borderRadius: 4, fontSize: 10 }}>
                          python main.py</code> in phone-launce/
                      </div>
                    </div>
                  ) : bridgeOnline === null ? (
                    <div style={{ fontSize: 11, color: "#6d758b" }}>Checking phone bridge...</div>
                  ) : bridgeDevices.length === 0 ? (
                    <div style={{ fontSize: 11, color: "#6d758b", lineHeight: 1.7 }}>
                      <span style={{ fontSize: 13 }}>🔌</span> No phone connected
                      <div style={{ marginTop: 4, color: "#4b5563" }}>
                        Connect Android via USB and enable USB Debugging
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Device selector (only if multiple) */}
                      {bridgeDevices.length > 1 && (
                        <div style={{ marginBottom: 8 }}>
                          <select
                            value={bridgeDevice}
                            onChange={e => setBridgeDevice(e.target.value)}
                            style={{
                              width: "100%", background: "#1e293b", border: "1px solid rgba(189,178,255,0.15)",
                              borderRadius: 6, color: "#dde5ff", padding: "5px 8px", fontSize: 11,
                              fontFamily: "'Space Grotesk', sans-serif",
                            }}
                          >
                            {bridgeDevices.map(d => (
                              <option key={d.serial} value={d.serial}>
                                {d.transport === "wifi" ? "📶" : "🔌"} {d.name || d.serial}
                                {!d.authorized ? " (unauthorized)" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Single device indicator */}
                      {bridgeDevices.length === 1 && (
                        <div style={{ fontSize: 10, color: "#6d758b", marginBottom: 6 }}>
                          {bridgeDevices[0].transport === "wifi" ? "📶" : "🔌"} {bridgeDevices[0].name || bridgeDevices[0].serial}
                        </div>
                      )}

                      {/* Call / End call button */}
                      {bridgeCallState === "idle" ? (
                        <button
                          onClick={() => handleBridgeCall(selectedLead.phone!)}
                          disabled={!bridgeDevice || !bridgeDevices.find(d => d.serial === bridgeDevice)?.authorized}
                          style={{
                            width: "100%", padding: "7px 0", borderRadius: 8, border: "none",
                            background: "linear-gradient(135deg, #22c55e, #16a34a)", color: "#fff",
                            fontSize: 12, fontWeight: 700, cursor: "pointer",
                            fontFamily: "'Space Grotesk', sans-serif",
                            opacity: (!bridgeDevice || !bridgeDevices.find(d => d.serial === bridgeDevice)?.authorized) ? 0.4 : 1,
                          }}
                        >
                          📞 Call {selectedLead.phone}
                        </button>
                      ) : bridgeCallState === "dialing" ? (
                        <div style={{
                          textAlign: "center", padding: "7px 0", fontSize: 12,
                          color: "#fbbf24", fontWeight: 600,
                        }}>
                          <span className="spinner" style={{ width: 12, height: 12, display: "inline-block", marginRight: 6 }} />
                          Dialing {selectedLead.phone}...
                        </div>
                      ) : (
                        <div>
                          <div style={{
                            display: "flex", alignItems: "center", gap: 6, marginBottom: 6,
                            fontSize: 11, color: "#4ade80", fontWeight: 700,
                          }}>
                            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px #22c55e" }} />
                            On call — {bridgeCallNum}
                          </div>
                          <button
                            onClick={handleBridgeEndCall}
                            style={{
                              width: "100%", padding: "7px 0", borderRadius: 8, border: "none",
                              background: "#ef4444", color: "#fff",
                              fontSize: 12, fontWeight: 700, cursor: "pointer",
                              fontFamily: "'Space Grotesk', sans-serif",
                            }}
                          >
                            📵 End Call
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {selectedLead.email    && <div><span className="crm-label">✉️</span>{selectedLead.email}</div>}
              {selectedLead.country  && <div><span className="crm-label">🌍</span>{selectedLead.country}</div>}
              {selectedLead.language && <div><span className="crm-label">🗣️</span>{selectedLead.language}</div>}
              {selectedLead.source   && (
                <div>
                  <span className="crm-label">📡</span>
                  {SOURCE_LABELS[selectedLead.source]
                    ? <><span>{SOURCE_LABELS[selectedLead.source].icon}</span> {SOURCE_LABELS[selectedLead.source].label}</>
                    : selectedLead.source}
                </div>
              )}
              {selectedLead.assigned_to && <div><span className="crm-label">👤</span>{selectedLead.assigned_to}</div>}
              {selectedLead.estimated_value > 0 && <div><span className="crm-label">💰</span>{fmt(selectedLead.estimated_value)}</div>}
              <div className="crm-detail-timestamps">
                <span title={fmtDate(selectedLead.created_at)}>
                  📅 Created {relTime(selectedLead.created_at)}
                </span>
                {selectedLead.last_activity_at && (
                  <span title={fmtTime(selectedLead.last_activity_at)}>
                    · Active {relTime(selectedLead.last_activity_at)}
                  </span>
                )}
              </div>
            </div>

            {/* Badges row */}
            <div className="crm-detail-badges">
              <span className={`crm-imp-badge ${IMPORTANCE_CONFIG[selectedLead.importance]?.cls}`}>
                {IMPORTANCE_CONFIG[selectedLead.importance]?.icon} {IMPORTANCE_CONFIG[selectedLead.importance]?.label}
              </span>
              <span className="crm-category-badge">
                {CATEGORY_CONFIG[selectedLead.category]?.icon} {CATEGORY_CONFIG[selectedLead.category]?.label}
              </span>
              <span className="crm-score-badge">🤖 {selectedLead.ai_score.toFixed(0)}/100</span>
            </div>

            {/* Tags */}
            {selectedLead.tags?.length > 0 && (
              <div className="crm-detail-tags">
                {selectedLead.tags.map(t => <span key={t} className="crm-tag">{t}</span>)}
              </div>
            )}

            {/* AI Summary */}
            {selectedLead.ai_summary && (
              <div className="crm-detail-ai">
                <div className="crm-label">🤖 AI Analysis</div>
                <div className="crm-detail-ai-text">{selectedLead.ai_summary}</div>
              </div>
            )}

            {/* Status changer */}
            <div className="crm-detail-section">
              <div className="crm-label">Pipeline Stage</div>
              <div className="crm-status-buttons">
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <button
                    key={k}
                    className={`crm-status-btn ${selectedLead.status === k ? "crm-status-btn-active" : ""}`}
                    style={{ borderColor: v.color, color: selectedLead.status === k ? "#fff" : v.color, background: selectedLead.status === k ? v.color : "transparent" }}
                    onClick={() => handleStatusChange(selectedLead.id, k)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            {selectedLead.notes && (
              <div className="crm-detail-section">
                <div className="crm-label">Notes</div>
                <div className="crm-detail-notes">{selectedLead.notes}</div>
              </div>
            )}

            {/* Add note */}
            <div className="crm-detail-section">
              <div className="crm-label">Add Note / Activity</div>
              <textarea
                className="crm-note-input"
                placeholder="Type a note, call summary, or follow-up…"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                rows={2}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                {["note","call","email","whatsapp"].map(t => (
                  <button
                    key={t}
                    className="crm-activity-type-btn"
                    onClick={async () => {
                      if (!newNote.trim()) return;
                      await fetch(`/api/crm/leads/${selectedLead.id}/activities`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ activity_type: t, content: newNote, actor: "sales-team" }),
                      });
                      setNewNote("");
                      fetchActivities(selectedLead.id);
                    }}
                  >
                    {ACTIVITY_ICONS[t]} {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Activity timeline */}
            <div className="crm-detail-section crm-timeline">
              <div className="crm-label">Activity History</div>
              {actLoading ? (
                <div className="crm-muted" style={{ fontSize: 12 }}>Loading…</div>
              ) : activities.length === 0 ? (
                <div className="crm-muted" style={{ fontSize: 12 }}>No activity yet</div>
              ) : (
                <div className="crm-activity-list">
                  {activities.reduce<{ group: string; items: Activity[] }[]>((groups, a) => {
                    const g = dateGroup(a.created_at);
                    const last = groups[groups.length - 1];
                    if (last && last.group === g) { last.items.push(a); }
                    else { groups.push({ group: g, items: [a] }); }
                    return groups;
                  }, []).map(({ group, items }) => (
                    <div key={group}>
                      <div className="crm-timeline-date-label">{group}</div>
                      {items.map(a => (
                        <div key={a.id} className="crm-activity-item">
                          <div className="crm-activity-icon">{ACTIVITY_ICONS[a.activity_type] || "📋"}</div>
                          <div className="crm-activity-body">
                            <div className="crm-activity-header">
                              <span className="crm-activity-type-label">{a.activity_type.replace("_", " ")}</span>
                              <span className="crm-activity-time" title={fmtTime(a.created_at)}>
                                {relTime(a.created_at)}
                              </span>
                            </div>
                            <div className="crm-activity-content">{a.content}</div>
                            <div className="crm-activity-meta">
                              <span className="crm-activity-actor">by {a.actor}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="crm-detail-footer">
              <button className="crm-btn-primary" onClick={() => { setEditingLead(selectedLead); setShowForm(true); }}>
                ✏️ Edit Lead
              </button>
              <button className="crm-btn-danger" onClick={() => handleDelete(selectedLead.id)}>
                🗑️ Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Lead form modal ── */}
      {showForm && (
        <CRMLeadForm
          lead={editingLead}
          onClose={(refresh) => {
            setShowForm(false);
            setEditingLead(null);
            if (refresh) fetchLeads();
          }}
        />
      )}

      {/* ── Floating bulk-action bar ── */}
      {checkedLeads.size > 0 && (
        <div style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: "rgba(9,19,39,0.97)", backdropFilter: "blur(60px)",
          border: "0.5px solid rgba(189,178,255,0.3)", borderRadius: 16,
          padding: "12px 20px", display: "flex", alignItems: "center", gap: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)", zIndex: 200,
          fontFamily: "'Space Grotesk', sans-serif",
        }}>
          <span style={{ color: "#bdb2ff", fontWeight: 600, fontFamily: "'Space Grotesk',sans-serif" }}>
            {checkedLeads.size} selected
          </span>
          <button
            onClick={() => { setShowSendModal(true); setBulkResult(null); }}
            style={{
              background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
              border: "none", borderRadius: 8, color: "#fff",
              padding: "7px 16px", cursor: "pointer", fontWeight: 600,
              fontFamily: "'Space Grotesk', sans-serif", fontSize: 13,
            }}
          >
            💬 Send Message
          </button>
          <button
            onClick={() => setCheckedLeads(new Set())}
            style={{
              background: "none", border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 8, color: "#9ca3af", padding: "7px 12px",
              cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12,
            }}
          >
            ✕ Clear
          </button>
        </div>
      )}

      {/* ── Send message modal ── */}
      {showSendModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300,
        }}>
          <div style={{
            background: "rgba(9,19,39,0.98)", backdropFilter: "blur(60px)",
            border: "0.5px solid rgba(189,178,255,0.2)", borderRadius: 16,
            padding: 28, width: 480, maxWidth: "95vw",
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ color: "#dae2fd", margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 18 }}>
                💬 Send WhatsApp Message
              </h3>
              <button onClick={() => setShowSendModal(false)}
                style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>

            <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 16 }}>
              Sending to <strong style={{ color: "#a5b4fc" }}>{checkedLeads.size}</strong> selected lead(s)
            </div>

            {/* Message textarea */}
            <textarea
              placeholder="Type your message here…"
              value={bulkMsg}
              onChange={e => setBulkMsg(e.target.value)}
              rows={4}
              style={{
                width: "100%", background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8,
                color: "#dae2fd", padding: "10px 12px", resize: "vertical",
                fontFamily: "'Space Grotesk', sans-serif", fontSize: 14,
                boxSizing: "border-box",
              }}
            />

            {/* Image upload */}
            <div style={{ marginTop: 14 }}>
              <div style={{ color: "#9ca3af", fontSize: 12, marginBottom: 6 }}>
                📎 Attach image (optional)
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={e => e.target.files?.[0] && handleImagePick(e.target.files[0])}
              />
              <button
                onClick={() => imageInputRef.current?.click()}
                style={{
                  background: "rgba(255,255,255,0.05)", border: "1px dashed rgba(255,255,255,0.2)",
                  borderRadius: 8, color: "#9ca3af", padding: "8px 16px",
                  cursor: "pointer", fontSize: 13, width: "100%",
                }}
              >
                {bulkImageFile ? `📷 ${bulkImageFile.name}` : "Click to upload image from computer"}
              </button>
              {bulkImageUrl && (
                <div style={{ color: "#34d399", fontSize: 11, marginTop: 4 }}>
                  ✅ Image uploaded — ready to send
                </div>
              )}
              {bulkImageFile && !bulkImageUrl && (
                <div style={{ color: "#fbbf24", fontSize: 11, marginTop: 4 }}>
                  ⏳ Uploading…
                </div>
              )}
            </div>

            {/* Result — aggregated counts on top, per-status breakdown below.
                Backend at /api/crm/bulk-send returns results: [{status, ...}]
                where status ∈ sent | not_found | no_phone | failed | error.
                Surfacing the breakdown is the difference between "Failed: N
                (no clue why)" and "N had no phone, M not found, K errored". */}
            {bulkResult && (() => {
              const results = bulkResult.results ?? [];
              const noPhone = results.filter(r => r.status === "no_phone").length;
              const notFound = results.filter(r => r.status === "not_found").length;
              const failedNet = results.filter(r => r.status === "failed").length;
              const errored = results.filter(r => r.status === "error").length;
              const firstError = results.find(r => r.status === "error" && r.error)?.error;
              const allOk = bulkResult.failed === 0;
              return (
                <div style={{
                  marginTop: 14, padding: "10px 14px", borderRadius: 8,
                  background: allOk ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                  color: allOk ? "#4ade80" : "#f87171",
                  fontSize: 13,
                  fontFamily: "'Space Grotesk', sans-serif",
                  lineHeight: 1.5,
                }}>
                  <div style={{ fontWeight: 600 }}>
                    ✅ Sent: {bulkResult.sent} · ❌ Failed: {bulkResult.failed}
                  </div>
                  {!allOk && results.length > 0 && (
                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.92 }}>
                      {noPhone > 0 && <span>· {noPhone} lead{noPhone !== 1 ? "s" : ""} have no phone </span>}
                      {notFound > 0 && <span>· {notFound} not found </span>}
                      {failedNet > 0 && <span>· {failedNet} send rejected by WhatsApp </span>}
                      {errored > 0 && <span>· {errored} errored </span>}
                      {firstError && (
                        <div style={{ marginTop: 4, fontStyle: "italic", opacity: 0.85 }}>
                          first error: {firstError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowSendModal(false)}
                style={{
                  background: "none", border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 8, color: "#9ca3af", padding: "9px 18px",
                  cursor: "pointer", fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkSend}
                disabled={bulkSending || (!bulkMsg && !bulkImageUrl)}
                style={{
                  background: bulkSending ? "rgba(99,102,241,0.4)" : "linear-gradient(135deg,#6366f1,#8b5cf6)",
                  border: "none", borderRadius: 8, color: "#fff",
                  padding: "9px 24px", cursor: bulkSending ? "not-allowed" : "pointer",
                  fontWeight: 600, fontSize: 13,
                }}
              >
                {bulkSending ? "⏳ Sending…" : `📤 Send to ${checkedLeads.size} leads`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMLeadsList;
